import { FC, useEffect, useMemo, useState } from "react";
import { Box, Text, Flex } from "@smile2impress/components/relaunch";
import { keyframes } from "@smile2impress/components";
import { sanitizeHtml } from "@smile2impress/attraction-utils";

import { MainBannerSlogansProps } from "./MainBannerSlogans.types";

const keyframesTextSlide = keyframes`
    0% {
        transform: translateY(-150%);
    }
    15%,
    85% {
        transform: translateY(0%);
    }
    100% {
        transform: translateY(150%);
    }
`;

const SLOGAN_APPEARANCE_TIME = 3000;
const SLOGAN_INTERVAL_TIME = 500;

const animationTripleTextSlide = `${keyframesTextSlide} ${SLOGAN_APPEARANCE_TIME}ms linear forwards`;

export const MainBannerSlogans: FC<MainBannerSlogansProps> = ({
    slogans: rawSlogans = [],
}) => {
    const [activeSloganIndex, setSloganIndex] = useState(0);
    const slogans = useMemo(
        () =>
            rawSlogans
                .filter((slogan) => slogan.length > 0)
                .map((slogan) =>
                    slogan
                        .split(/\<br\s?\/\s?\>/g)
                        .map((line: string) => line.trim())
                )
                .map((lines: string[]) =>
                    lines.length > 1 ? lines : new Array(3).fill(lines[0])
                ),
        [rawSlogans]
    );

    const activeSlogan = useMemo(
        () => slogans[activeSloganIndex],
        [slogans, activeSloganIndex]
    );

    const rotateSlogans = () => {
        setSloganIndex((prev: number) => {
            if (prev < slogans.length - 1) return prev + 1;
            return 0;
        });
    };

    useEffect(() => {
        const interval = setInterval(
            rotateSlogans,
            SLOGAN_APPEARANCE_TIME + SLOGAN_INTERVAL_TIME
        );
        return () => clearInterval(interval);
    }, [slogans]);

    if (!activeSlogan) {
        return null;
    }

    return (
        <Flex direction="column" px={[0, 0, 2]} py={[0, 0, 2]}>
            <Box key={sanitizeHtml(activeSlogan[0].replace(/\s/g, ""))}>
                {activeSlogan.map((sloganLine, index: number) => (
                    <Box
                        overflow="hidden"
                        h={["52px", "52px", "84px"]}
                        key={index}
                    >
                        <Text
                            textStyle={["H2", "H2", "H1"]}
                            lineHeight={["125%", "125%", "120%"]}
                            color="text.baseInverse"
                            animation={[animationTripleTextSlide]}
                        >
                            {sloganLine}
                        </Text>
                    </Box>
                ))}
            </Box>
        </Flex>
    );
};
